import {
  concat,
  difference,
  drop,
  filter,
  find,
  first,
  flatMap,
  forEach,
  includes,
  isEmpty,
  isString,
  keys,
  map,
  size,
  some,
  uniq,
} from 'es-toolkit/compat';
import { pipe, replace } from 'lodash/fp';
import moment from 'moment';
import papaparse from 'papaparse';
import { stringify } from 'qs';

import { getLatestPerSlideStudyCustomerResults, getStudyCustomerResultsByJobId } from 'api/customerResults';
import { getIntermediateResultPresignedUrl } from 'api/experimentResults';
import { getFeatureKeysByStudyAndOrchestration } from 'api/features';
import { getProcedures } from 'api/procedures';
import { ResultsMode } from 'interfaces/experimentResults';
import { FeatureFormatter } from 'interfaces/featureFormatter';
import { Procedure } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { apiRequestHandlerPromise } from 'utils/apiRequestHandler';
import { findFormatterByKey, getFeatureDisplayName, getFeatureFormattedValue } from 'utils/features';
import { getFeaturesKeysObj } from 'utils/features/getFeaturesKeysObjects';
import { isBrackets } from 'utils/formatBrackets';
import { FormatBracketsOptions } from 'utils/formatBrackets/formatBracketsOptions';
import { getStudyStainTypeIds } from './queryHooks/stainType/useStudyStainTypes';
import { casesOptions, casesSchema } from './useCasesParams/constants';
import { CasesParams } from './useCasesParams/types';
import { exportFeaturesSchema } from './useEncodedFilters/constants';
import { encodeQueryParamsUsingSchema } from './useEncodedFilters/helpers';

const TEX_REGEX_1 = /\\mu ?/g;
const TEX_REGEX_2 = /\^2/g;
const replaceTeXWithUnicode = pipe(replace(TEX_REGEX_1, 'µ'), replace(TEX_REGEX_2, '²')); // a bit of a hack to replace TeX with unicode

const toCsvBlob = (data: string[][]) =>
  new Blob(['\ufeff' + papaparse.unparse(data)], { type: 'text/csv;charset=utf-8;' });

const downloadDataAsCSV = (data: string[][], fileNamePrefix: string) => {
  const blob = toCsvBlob(data);
  const date = moment().format('DD.MM:YYYY hh:mm:ss a');
  const fileName = `${fileNamePrefix}-${date}.csv`;
  downloadBlob(blob, fileName);
};

export { downloadDataAsCSV };

type ExportContext = FormatBracketsOptions & { skipPreloadingStudyStains?: boolean; labId?: string; studyId?: string };

const fetchExportRows = async ({
  endpoint,
  queryParams,
  context,
  signal,
}: {
  context?: ExportContext;
  endpoint: string;
  queryParams: Record<string, string | boolean> | string;
  signal?: AbortSignal;
}) => {
  if (context?.skipPreloadingStudyStains) {
    // If we did not query stains before, we need to do it now to know whether to add stain to the export
    const labId = context.labId;
    const studyId = context.studyId;

    const studyStains = await getStudyStainTypeIds({ labId, studyId });

    if (size(studyStains) > 1) {
      context.addStain = true;
    }
  }
  const url = `${endpoint}?${isString(queryParams) ? queryParams : stringify(queryParams)}`;
  try {
    const result = await apiRequestHandlerPromise({
      url,
      method: 'GET',
      signal,
    });
    const resultObj: { data: string[][] } = papaparse.parse(result.csv_data);
    return context ? formatResults(resultObj, context) : resultObj.data;
  } catch (error) {
    console.error(`Got an error exporting data: ${error}`);
    throw error;
  }
};

const exportData = async ({
  fileNamePrefix,
  ...request
}: Parameters<typeof fetchExportRows>[0] & { fileNamePrefix: string }) => {
  downloadDataAsCSV(await fetchExportRows(request), fileNamePrefix);
};

export const downloadFileUrlWithFileName = async (url: string, fileName: string) => {
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  downloadFileUrlWithFileName(url, fileName);
};

export const exportIntermediateResultParquetUnsignedUrl = async ({
  studyId,
  orchestrationId,
  slideId,
  unsignedUrl,
  fileName,
}: {
  studyId: string;
  orchestrationId: string;
  slideId: string;
  unsignedUrl: string;
  fileName: string;
}) => {
  try {
    const signedUrl = await getIntermediateResultPresignedUrl({
      studyId,
      orchestrationId,
      slideId,
      unsignedUrl,
    });

    const result = await fetch(signedUrl);
    const blob = await result.blob();
    downloadBlob(blob, fileName);
  } catch (error) {
    console.error(`Got an error exporting intermediate result parquet data: ${error}`);
  }
};

export interface ProcedureMetadataCsvColumn {
  header: string;
  getValue: ({ procedure, slide }: { procedure: Procedure; slide: Slide }) => unknown;
}

const procedureMetadataExcludedHeaders = ['slides', 'clinicalData', 'features', 'presentationInfo'];
const slideMetadataExcludedHeaders = ['registrations', 'experimentResults', 'slideTagAssignments'];

const normalizeCsvCellValue = (value: unknown) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (!isString(value)) {
    return JSON.stringify(value);
  }

  return value;
};

const formatObjectDataForCsv = <Data extends object>(data: Data, headers: string[]): string[] => {
  return map(headers, (header) => normalizeCsvCellValue(data[header as keyof Data]));
};

export const buildProcedureMetadataCsvData = ({
  procedures,
  additionalColumns = [],
}: {
  procedures: Procedure[];
  additionalColumns?: ProcedureMetadataCsvColumn[];
}) => {
  const firstProcedure = first(procedures);

  if (!firstProcedure) {
    return [];
  }

  const firstSlide = first(firstProcedure.slides || []);
  const procedureHeaders = difference(keys(firstProcedure), procedureMetadataExcludedHeaders);
  const slideHeaders = firstSlide ? difference(keys(firstSlide), slideMetadataExcludedHeaders) : [];
  const csvHeaders = concat(
    map(additionalColumns, 'header'),
    map(procedureHeaders, (procedureHeader) => `case.${procedureHeader}`),
    map(slideHeaders, (slideHeader) => `slide.${slideHeader}`)
  );

  return concat(
    [csvHeaders],
    flatMap(procedures, (procedure: Procedure) => {
      const relevantProcedureData = formatObjectDataForCsv(procedure, procedureHeaders);

      return map(procedure.slides || [], (slide: Slide) => {
        const additionalColumnValues = additionalColumns.map((additionalColumn) =>
          normalizeCsvCellValue(additionalColumn.getValue({ procedure, slide }))
        );
        const relevantSlideData = formatObjectDataForCsv(slide, slideHeaders);

        return concat(additionalColumnValues, relevantProcedureData, relevantSlideData);
      });
    })
  );
};

export const exportProcedureMetadataCsv = async ({
  studyId,
  encodedFilters,
  fileNamePrefix = `studyId-${studyId}-cases`,
  additionalColumns = [],
  signal,
}: {
  studyId: string;
  encodedFilters: string;
  fileNamePrefix?: string;
  additionalColumns?: ProcedureMetadataCsvColumn[];
  signal?: AbortSignal;
}) => {
  const proceduresResponse = await getProcedures(encodedFilters, signal);
  const procedures = proceduresResponse.procedures;

  if (isEmpty(procedures)) {
    console.warn('No cases to export');
    return [];
  }

  const proceduresDataForExport = buildProcedureMetadataCsvData({ procedures, additionalColumns });
  downloadDataAsCSV(proceduresDataForExport, fileNamePrefix);

  return proceduresDataForExport;
};

export const exportProcedures = async (studyId: string, encodedFilters: string, signal?: AbortSignal) => {
  return exportProcedureMetadataCsv({
    studyId,
    encodedFilters,
    fileNamePrefix: `studyId-${studyId}-cases`,
    signal,
  });
};

interface ExportFeaturesOptions {
  studyId: string;
  context: FormatBracketsOptions;
  highlightedOnly?: boolean;
  resultsMode?: ResultsMode;
  orchestrationId?: string;
  hideExcluded?: boolean;
  casesParams?: Partial<CasesParams>;
  signal?: AbortSignal;
}

const buildFeaturesRequest = ({
  studyId,
  context,
  highlightedOnly,
  resultsMode,
  orchestrationId,
  hideExcluded,
  casesParams,
  signal,
}: ExportFeaturesOptions) => {
  const exportFeaturesParams = {
    studyId,
    ...(highlightedOnly && { highlightedFeaturesOnly: 'true' }),
    ...(resultsMode && { resultsMode }),
    ...(resultsMode === ResultsMode.Manual && { orchestrationId }),
    ...(hideExcluded && { hideExcluded }),
    ...(Boolean(casesParams) && casesParams),
  };
  const encodedFilters = encodeQueryParamsUsingSchema(
    exportFeaturesParams,
    {
      ...exportFeaturesSchema,
      ...(Boolean(casesParams) && casesSchema),
    },
    casesParams ? casesOptions : undefined
  );
  return { endpoint: 'features', queryParams: encodedFilters, context, signal };
};

export const exportFeatures = (options: ExportFeaturesOptions) =>
  exportData({ ...buildFeaturesRequest(options), fileNamePrefix: `studyId-${options.studyId}` });

// The same request as exportFeatures, returned as a CSV file instead of handed to the browser.
export const fetchFeaturesCsvBlob = async (options: ExportFeaturesOptions) =>
  toCsvBlob(await fetchExportRows(buildFeaturesRequest(options)));

export const exportFormResults = async (formId: string, signal?: AbortSignal) => {
  return exportData({
    endpoint: `forms/${formId}/responses/aggregated_table`,
    queryParams: { formId },
    fileNamePrefix: `formId-${formId}`,
    signal,
  });
};

export const exportQcResults = ({
  labId,
  studyId,
  orchestrationId,
  resultsMode,
  context,
  signal,
}: {
  labId: string;
  studyId: string;
  orchestrationId?: string;
  resultsMode?: ResultsMode;
  context: FormatBracketsOptions & { skipPreloadingStudyStains?: boolean; labId?: string; studyId?: string };
  signal?: AbortSignal;
}) => {
  return exportData({
    context,
    endpoint: 'label_results',
    queryParams: { filters: JSON.stringify({ studyId, orchestrationId }), resultsMode, labId },
    fileNamePrefix: `label_results_for_studyId-${studyId}`,
    signal,
  });
};

export const exportCustomerResults = async ({
  labId,
  studyId,
  jobId,
  downloadImmediately,
}: {
  labId: string;
  studyId: string;
  jobId: string;
  downloadImmediately?: boolean;
}) => {
  try {
    const presignedResultUrls = await getStudyCustomerResultsByJobId({
      studyId,
      jobId,
      labId,
    });
    if (downloadImmediately) {
      forEach(presignedResultUrls, (presignedUrl) => {
        window.open(presignedUrl);
      });
    }
    return presignedResultUrls;
  } catch (error) {
    console.error('Got an error getting customer results', error);
    throw error;
  }
};

export const exportLatestPerSlideCustomerResults = async ({
  labId,
  studyId,
  downloadImmediately,
}: {
  labId: string;
  studyId: string;
  downloadImmediately?: boolean;
}) => {
  try {
    const presignedResultUrls = await getLatestPerSlideStudyCustomerResults({
      studyId,
      labId,
    });
    if (downloadImmediately) {
      forEach(presignedResultUrls, (presignedUrl) => {
        window.open(presignedUrl);
      });
    }
    return presignedResultUrls;
  } catch (error) {
    console.error('Got an error getting customer results', error);
    throw error;
  }
};

export const exportFeatureNames = async ({
  studyId,
  context,
  orchestrationId,
}: {
  studyId: string;
  context: FormatBracketsOptions;
  orchestrationId?: string;
}) => {
  const featuresKeys = (
    await getFeatureKeysByStudyAndOrchestration(studyId, orchestrationId, false, false, ResultsMode.Manual)
  ).featuresKeys;
  const featuresKeysObj = getFeaturesKeysObj(featuresKeys, context);

  const csvData = papaparse.unparse(featuresKeysObj);
  const blob = new Blob(['\ufeff' + csvData], { type: 'text/csv;charset=utf-8;' });
  const urlDownload = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('href', urlDownload);
  const date = moment().format('DD.MM:YYYY hh:mm:ss a');
  const fileName = `features names for studyId-${studyId} ${date}`;
  a.setAttribute('download', `${fileName}.csv`);
  a.click();

  return featuresKeysObj;
};

const stainColumnIndex = 3;

export function formatResults(resultObj: { data: string[][] }, context: FormatBracketsOptions) {
  const resultRows = filter(drop(resultObj.data, 1), (row) => some(row, (cell) => cell !== ''));

  // Get unique stains without header and empty values
  const stains = uniq(
    map(
      filter(resultRows, (row) => !isEmpty(row[stainColumnIndex])),
      (row) => row[stainColumnIndex]
    )
  );

  const headersFeatureFormatters: (FeatureFormatter & { nonFormattedUnits?: string })[] = [];
  const exportFormattingContext = { ...context, addStain: size(stains) > 1 };

  const resultHeaderFormatted = map(resultObj.data[0], (header: string) => {
    const featureDisplayName = isBrackets(header) ? getFeatureDisplayName(header, exportFormattingContext) : header;
    const headerFeatureFormatter = findFormatterByKey(header, {
      featureDisplayName,
      context: exportFormattingContext,
    });

    headersFeatureFormatters.push(headerFeatureFormatter);
    let unicodeFormattedUnits = '';

    if (includes(headerFeatureFormatter?.valueFormat, '%')) {
      unicodeFormattedUnits = ` [%]`;
    } else if (headerFeatureFormatter?.nonFormattedUnits) {
      unicodeFormattedUnits = ` [${replaceTeXWithUnicode(headerFeatureFormatter.nonFormattedUnits)}]`;
    }

    return `${headerFeatureFormatter.nameOverride || featureDisplayName}${unicodeFormattedUnits}`;
  });

  const resultValueFormatted = map(resultRows, (row) => {
    return map(row, (cell, cellIndex) => {
      const formatter = headersFeatureFormatters[cellIndex];
      if (!isEmpty(formatter)) {
        return getFeatureFormattedValue(cell, formatter?.exportValueFormat || formatter?.valueFormat, null, true);
      }
      return cell;
    });
  });

  const formattedFeatureColumnIndexByName = new Map<string, number>();
  const consolidatedColumns: Array<{ header: string; sourceColumnIndexes: number[] }> = [];

  forEach(resultHeaderFormatted, (formattedHeader, sourceColumnIndex) => {
    const isFeatureColumn = isBrackets(resultObj.data[0][sourceColumnIndex]);
    const existingColumnIndex = isFeatureColumn ? formattedFeatureColumnIndexByName.get(formattedHeader) : undefined;

    if (existingColumnIndex !== undefined) {
      consolidatedColumns[existingColumnIndex].sourceColumnIndexes.push(sourceColumnIndex);
      return;
    }

    const consolidatedColumnIndex = consolidatedColumns.length;
    consolidatedColumns.push({ header: formattedHeader, sourceColumnIndexes: [sourceColumnIndex] });

    if (isFeatureColumn) {
      formattedFeatureColumnIndexByName.set(formattedHeader, consolidatedColumnIndex);
    }
  });

  const consolidatedHeaders = map(consolidatedColumns, ({ header }) => header);
  const consolidatedValues = map(resultValueFormatted, (row) =>
    map(consolidatedColumns, ({ sourceColumnIndexes }) => {
      const populatedCellValue = find(sourceColumnIndexes, (sourceColumnIndex) => {
        const cellValue = row[sourceColumnIndex];
        return cellValue !== undefined && cellValue !== null && cellValue !== '';
      });

      return populatedCellValue === undefined ? '' : row[populatedCellValue] ?? '';
    })
  );

  return [consolidatedHeaders, ...consolidatedValues];
}
